import React, { useRef, useState } from 'react';

import { useHistory } from 'react-router';
import styled from 'styled-components';

import * as actions from 'actions';
import { PERMISSION_SCOPE } from 'Components-redesign/PermissionChecker/consts';
import { SUBUSER_TOKEN_KEY } from 'consts';
import { Dispatch, useDispatch, useI18n, useSelector } from 'Hooks';
import LegalEntitySelector from 'Scenes/Adyen/Transactions/Components/LegalEntitySelector';
import { hasActiveIntegration } from 'utils/integrations';
import { canAccessPrivateMenuItems } from 'utils/userRoleUtils';

import DrawerGroup from './DrawerGroup';
import { PRODUCT_TYPE } from '../../../__queries__/globalTypes';

const DrawerWrapper = styled.div<{ hasMenuOpen: boolean }>`
  width: ${({ hasMenuOpen }) => (hasMenuOpen ? '360px' : '88px')};
  overflow: hidden;
  position: fixed;
  top: 80px;
  bottom: 0;
  z-index: 999;

  &:before {
    content: '';
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    width: 88px;
    pointer-events: none;
    background-color: #ffff;
    box-shadow: 8px 0px 24px 8px rgb(0 0 0 / 16%), 2px 0px 8px 0px rgb(0 0 0 / 16%);
  }
`;

const Drawer = styled.div<{ hasMenuOpen: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  bottom: 0;
  overflow: auto;
  right: -17px;
  display: flex;
  flex-flow: column nowrap;
  z-index: 999;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const BetaBox = styled.span`
  color: #fff;
  border-radius: 4px;
  margin-left: 7px;
  padding: 5px 6px;
  background: #4468a8;
  font-size: 13px;
`;

interface DrawerComponentProps {
  routes: string[];
}

export type link = string;
export type title = string | Function | null;
export type isPrivate = boolean | null;
export type onTouch = (dispatch: Dispatch) => void | null;
export type main = boolean | null;

export type SectionItem = [link, title, isPrivate?, onTouch?, main?];

export interface Section {
  id: string;
  title: string;
  main?: boolean;
  private?: boolean;
  items: SectionItem[];
  permissions?: PERMISSION_SCOPE[];
}

export const getPrivateLayout: (t: (str: string) => string) => Section[] = (t) => [
  {
    id: 'select',
    main: true,
    title: t('General.Home'),
    items: [['', null, false, undefined, true]],
  },
  {
    id: 'companies',
    main: true,
    title: t('General.Companies'),
    items: [['companies', null, false, undefined, true]],
  },
  {
    id: 'create-company',
    main: true,
    title: t('CreateCompany.Title'),
    items: [['create-company', null, false, undefined, true]],
  },
  {
    id: 'organizations',
    main: true,
    title: "Org's",
    items: [['organizations', null, false, undefined, true]],
  },
  {
    id: 'adyen',
    title: t('General.Adyen'),
    items: [
      ['adyen/balance-onboarding', 'Onboarding & Accounts'],
      ['adyen/payments', t('General.Payments')],
      ['adyen/terminals', t('General.Terminals')],
      ['adyen/split-configs', t('General.SplitConfigs')],
    ],
  },
  {
    id: 'licenses',
    main: true,
    title: t('General.Licenses'),
    items: [['licenses', null, false, undefined, true]],
  },
  {
    id: 'devices',
    main: true,
    title: t('General.Devices'),
    items: [['devices', null, false, undefined, true]],
  },
  {
    id: 'printers',
    main: true,
    title: t('General.Printers'),
    items: [['printers', null, false, undefined, true]],
  },
  {
    id: 'integrations',
    title: t('General.Integrations'),
    main: true,
    items: [
      ['integrations/list', t('General.IntegrationList')],
      ['integrations/configs', t('General.Configurations')],
      ['integrations/vouchers', t('Accounting.Vouchers')],
      ['integrations/invoices', t('Accounting.Invoices')],
      ['integrations/send-vouchers', t('Accounting.SendVouchers')],
      ['integrations/gstock', t('Accounting.GStock')],
      ['integrations/personalkollen', 'Personalkollen'],
    ],
  },
  {
    id: 'staffManagement',
    main: true,
    title: t('General.Admins'),
    items: [['admins', null, false, undefined, true]],
  },
  {
    id: 'errors',
    title: t('General.Errors'),
    main: true,
    items: [
      ['errors/integrations', t('General.Integrations')],
      ['errors/invalid-orders', t('Accounting.InvalidOrders')],
      ['errors/invalid-refunds', t('Accounting.InvalidRefunds')],
    ],
  },
  {
    id: 'choice',
    main: true,
    title: 'Choice',
    items: [['choice-invite-codes', 'Invite Codes']],
  },
  {
    id: 'internalTools',
    title: 'Tools',
    items: [
      ['tools/ordercreator', t('General.OrderCreator')],
      ['tools/graveyard', t('General.Graveyard')],
      ['tools/tableservice', t('General.TableService')],
      ['tools/voucher-comparison', 'Voucher comparison'],
      ['tools/companies-table', 'ID finder'],
      ['tools/licenses-migration', 'Migration to New Licenses'],
      ['tools/adyen-payouts-inspector', t('General.AdyenPayoutsInspector')],
      ['tools/adyen-transactions-inspector', t('General.AdyenTransactionsInspector')],
      ['tools/staff-extractor', 'Staff-Extractor'],
      ['tools/analytics-import', 'Analytics Import'],
      ['tools/cloud-print-debug', 'Cloud Print Debug'],
    ],
  },
  {
    id: 'monitoring',
    title: t('General.Monitoring'),
    items: [
      ['logs', t('General.ActionLogs')],
      ['notification-logs', t('General.NotificationLogs')],
      ['orders', t('General.Orders')],
      ['z-reports/debug', t('General.ZReportsDebug')],
    ],
  },
  {
    id: 'gift-cards',
    main: true,
    title: t('GiftCards.GiftCards'),
    items: [['gift-cards/admin', null, false, undefined, true]],
  },
  {
    id: 'onboarding',
    title: 'Onboarding',
    items: [
      ['onboarding/manual-user-activation', 'Manual user activation'],
      ['onboarding/monitoring', 'Monitor company progress'],
    ],
  },
  {
    id: 'revenue',
    main: true,
    title: t('General.Revenue'),
    items: [['revenue', null, false, undefined, true]],
  },
  {
    id: 'quicksight',
    title: 'Quicksight',
    items: [
      ['quicksight/namespaces', 'Namespaces'],
      ['quicksight/access-rights', 'Access Rights'],
    ],
  },
  {
    id: 'releases',
    main: true,
    title: 'Releases',
    items: [['releases', null, false, undefined, true]],
  },
];

// Do not use main: true in this LAYOUT
export const getLayout: (t: (str: string) => string) => Section[] = (t) => [
  {
    id: 'dashboard',
    title: t('General.Dashboard'),
    main: true,
    items: [['dashboard', t('General.Dashboard')]],
    permissions: [PERMISSION_SCOPE.FINANCE],
  },
  {
    id: 'app',
    title: t('General.App'),
    items: [
      ['app/members', t('General.Members')],
      ['app/memberships', t('General.Memberships')],
      ['app/assign-points', t('General.AssignPoints')],
      ['app/send-csv', t('General.SendCSV')],
      ['contents', t('General.Content')],
    ],
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION],
  },
  {
    id: 'engagement',
    title: t('General.Engagement'),
    items: [
      ['offers', t('General.Offers')],
      ['campaigns', t('General.Campaigns')],
      ['stampcards', t('General.StampCards')],
      ['signupcodes', t('General.SignupCodes')],
      ['promocodes', t('General.PromoCodes')],
      ['notifications', t('General.Notifications')],
      ['segments', t('General.Segments')],
    ],
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION],
  },
  {
    id: 'gift-cards-beta',
    title: t('GiftCards.GiftCards'),
    items: [['gift-cards-beta', t('GiftCards.GiftCards')]],
    main: true,
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION],
  },
  {
    id: 'storeManagement',
    title: t('General.Store'),
    items: [
      ['venues', t('General.Venues')],
      ['menus-select', t('General.Menus')],
      ['opening-hours', t('General.OpeningHours')],
      ['payment-methods', t('General.PaymentMethods')],
      ['receiptTemplates', t('General.ReceiptTemplates')],
      ['messages', t('General.PredefinedMessages')],
      ['stations', t('General.Stations')],
      ['beepers', t('General.Beepers')],
    ],
    permissions: [
      PERMISSION_SCOPE.POS_CUSTOMIZATION,
      PERMISSION_SCOPE.SETTINGS,
      PERMISSION_SCOPE.MENUS,
    ],
  },
  {
    id: 'pimpos',
    title: t('General.PIMPOS'),
    items: [['pim', 'PIM']],
    main: true,
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION, PERMISSION_SCOPE.MENUS],
  },
  {
    id: 'tsr',
    title: t('General.TSR'),
    items: [
      ['tsr/floorplans', t('General.Floorplans')],
      ['tsr/tables', t('General.Tables')],
      ['tsr/zones', t('General.Zones')],
      ['tsr/monitoring', t('General.TSRMonitoring')],
      ['tsr/messages', t('General.PredefinedMessages')],
    ],
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION],
  },
  {
    id: 'express',
    title: t('General.Express'),
    main: true,
    items: [['express-pages', t('General.ExpressPages')]],
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION],
  },
  {
    id: 'monitoring',
    title: t('General.Monitoring'),
    items: [
      ['orders', t('General.Orders')],
      ['inventory', t('General.Inventory')],
    ],
    permissions: [PERMISSION_SCOPE.POS_CUSTOMIZATION, PERMISSION_SCOPE.ORDERS],
  },
  {
    id: 'reporting',
    title: t('General.Reports'),
    items: [
      ['reports', t('General.Reports')],
      ['pos-item-report', t('General.POSItemReport')],
      ['gift-cards-beta/reports', t('Reports.GiftCardReports')],
    ],
    permissions: [PERMISSION_SCOPE.FINANCE],
  },
  {
    id: 'staffManagement',
    title: t('General.Staff'),
    items: [
      ['admins', t('General.Admins')],
      ['access-levels', t('General.AccessLevels')],
      ['staffs', t('General.Staff')],
      ['staff-report', t('General.StaffReport')],
    ],
    permissions: [PERMISSION_SCOPE.STAFF, PERMISSION_SCOPE.ACCOUNTS],
  },
];

const getLogOutRow: (t: (str: string) => string) => Section = (t) => ({
  id: 'logOut',
  title: t('General.LogOut'),
  main: true,
  items: [['', t('General.LogOut'), false, undefined, true]],
});

const getSuperUserRow: (t: (str: string) => string) => Section = (t) => ({
  id: 'exit',
  title: t('General.Exit'),
  main: true,
  items: [['', t('General.Exit'), false, undefined, true]],
});

const getSuperUserTools: (t: (str: string) => string) => Section = (t) => ({
  id: 'super-admin-tools',
  title: 'Super admin tools',
  main: true,
  items: [['super-admin-tools', 'Super admin tools', false, undefined, true]],
});

const ProTag = styled.div`
  padding: 2px 7px;

  /* Pink blue gradient */
  background: linear-gradient(90deg, #ff5f6d 0%, #ffc371 100%);
  border-radius: 4px;
  color: #333;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  margin-top: 3px;
`;

const DrawerComponent = ({ routes }: DrawerComponentProps) => {
  const [usesAdyenBalance, setUsesAdyenBalance] = useState<boolean>(false);

  const { t } = useI18n();
  const history = useHistory();
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const [menuItemOpen, setMenuItemOpen] = useState<string>('');

  const hasSubUser = useSelector((state) => state.subuserAuthToken);
  const companyData = useSelector((state) => state.companyData);
  const licenses = useSelector((state) => state.licenses);
  const userPermissions = useSelector((state) => state.userPermissions);

  const role = useSelector((state) => state.userRole);
  const isSuperAdmin = canAccessPrivateMenuItems(role);
  const superAdminUserType = useSelector((state) => state.superAdminUserType);

  const privateMode = !hasSubUser && isSuperAdmin;

  let activeLayout;
  const hasSKVIntegration = hasActiveIntegration('SKATTEVERKET');

  if (privateMode) {
    activeLayout = getPrivateLayout(t);

    /** Filter out the old devices page to prevent users from creating them in the wrong place */
    if (companyData?.useNewLicenses) {
      activeLayout = activeLayout.filter((section) => section.id !== 'devices');
    }
  } else {
    // Deep copy so we don't modify the base array
    activeLayout = JSON.parse(JSON.stringify(getLayout(t)));

    activeLayout
      .find((section) => section.id === 'reporting')
      .items.push(['z-reports', t('General.ZReport')]);

    if (usesAdyenBalance) {
      activeLayout
        .find((section) => section.id === 'reporting')
        .items.push(['transactions/reports', t('General.AdyenReports')]);

      activeLayout
        .find((section) => section.id === 'monitoring')
        .items.push(['transactions-list', t('General.AdyenTransactions')]);
    }

    if (licenses?.length > 0) {
      licenses.map((license) => {
        switch (license.plan) {
          case PRODUCT_TYPE.INSIGHTS:
            activeLayout.splice(3, 0, {
              id: 'insights',
              title: (
                <span>
                  Insights <ProTag>Pro</ProTag>
                </span>
              ),
              items: [['insights', 'Dashboard']],
              main: true,
              permissions: [PERMISSION_SCOPE.FINANCE],
            });
            break;
        }
      });
    }
  }

  activeLayout = activeLayout.concat(hasSubUser ? getSuperUserRow(t) : getLogOutRow(t));

  const handleClose = () => {
    setMenuItemOpen('');
  };

  const handleClick = (section: Section) => {
    if (section.id == 'exit') {
      dispatch(actions.setSubuserToken(null));
      localStorage.removeItem(SUBUSER_TOKEN_KEY);
    } else if (section.id == 'logOut') {
      dispatch(actions.setToken(null));
    }

    if (section.id == menuItemOpen) {
      handleClose();
    } else if (section.items && section.items.length > 1) {
      setMenuItemOpen(section.id);
    } else {
      const to = `/${section.items[0][0]}`;
      history.push(to);
      handleClose();
    }
  };

  const isBarcontrol = superAdminUserType === 'BARCONTROL';
  const isNorwegian = companyData?.country === 'NO';
  const norwayDisabledSections = ['dashboard', 'revenue', 'staffManagement', 'quicksight'];

  const isSpainUser = superAdminUserType === 'SPAIN';
  const isSpanish = companyData?.country === 'ES';
  const spainDisabledSections = ['dashboard', 'revenue', 'staffManagement', 'quicksight'];

  // Calculate restricted sections based on permissions
  const nonPermittedSections =
    userPermissions?.scopes.length === 0
      ? [] // This equals unrestricted access - so no restrictions applied
      : activeLayout
          .filter((section) => {
            if (!section.permissions || section.permissions.length === 0) {
              return false; // Section requires no specific permissions, so allow it
            }
            // Check if the user has any of the required permissions for this section
            const userHasPermission = section.permissions.some((scope) =>
              userPermissions?.scopes.includes(scope),
            );
            return !userHasPermission; // Include section if the user lacks required permissions
          })
          .map((section) => section.id);

  // Combine sections to hide: apply norwegian/bar control restrictions conditionally
  const disabledSections = new Set([
    ...nonPermittedSections,
    ...(isBarcontrol || isNorwegian ? norwayDisabledSections : []),
    ...(isSpainUser || isSpanish ? spainDisabledSections : []),
  ]);

  // Render the layout, filtering out disabled sections
  return (
    <DrawerWrapper hasMenuOpen={Boolean(menuItemOpen)}>
      <Drawer hasMenuOpen={Boolean(menuItemOpen)} ref={ref}>
        {hasSubUser && (
          <DrawerGroup
            companyData={companyData}
            section={getSuperUserTools(t)}
            handleClick={() => handleClick(getSuperUserTools(t))}
            menuItemOpen={menuItemOpen}
            handleClose={handleClose}
            routes={routes}
          />
        )}
        {activeLayout.map((section) => {
          const id = section.id;

          // If this section is restricted, skip rendering it
          if (disabledSections.has(id)) {
            return null;
          }

          return (
            <DrawerGroup
              key={section.title}
              companyData={companyData}
              section={section}
              handleClick={() => handleClick(section)}
              menuItemOpen={menuItemOpen}
              handleClose={handleClose}
              routes={routes}
            />
          );
        })}
      </Drawer>

      {/* Joar: Yes it's ugly to hide it like this. Fight me */}
      <div
        style={{
          display: 'none',
        }}>
        <LegalEntitySelector
          onChange={(id) => {
            setUsesAdyenBalance(!!id);
          }}
        />
      </div>
    </DrawerWrapper>
  );
};

export default DrawerComponent;
